<script setup>
import { computed, ref, watch } from 'vue'
import { useUiStore } from '@/stores/UiStore'
import { useLoadsStore } from '@/stores/Loads'
import { useToastStore } from '@/stores/Toast'
import { UPDATE_STATUS } from '@/constants/dispatch'

/**
 * The store instances.
 */
const uiStore = useUiStore()
const store = useLoadsStore()
const toastStore = useToastStore()

/**
 * The local refs.
 */
const show = ref(false)
const isLoading = ref(false)
const selectedStatus = ref(null)

/**
 * The load data.
 *
 * @return {Object}
 */
const data = computed(() => {
    return uiStore.actionLoads(UPDATE_STATUS)
})

/**
 * Determine if the action is active.
 *
 * @return {Boolean}
 */
const isActive = computed(() => {
    return uiStore.isActionActive(UPDATE_STATUS)
})

/**
 * The load pros.
 *
 * @return {String}
 */
const pros = computed(() => {
    if (Array.isArray(data.value)) {
        const LIMIT = 5

        const slicedArray =
            data.value.length > LIMIT ? data.value.slice(0, LIMIT) : data.value

        const result = slicedArray.map((load) => load.id).join(', ')

        return data.value.length > LIMIT
            ? `${result} and ${data.value.length - LIMIT} more`
            : result
    }

    return data.value?.id
})

/**
 * The load statuses.
 *
 * @return {Array}
 */
const statuses = computed(() => {
    return store.statuses
})

/**
 * The load status.
 *
 * @return {String}
 */
const status = computed(() => {
    if (Array.isArray(data.value)) {
        return data.value[0].status
    }

    return data.value?.status || ''
})

/**
 * The bulk update status.
 *
 * @return {Promise}
 */
const bulkUpdateStatus = async () => {
    try {
        isLoading.value = true

        const ids = data.value.map((load) => load.id)

        const promises = ids.map((id) => {
            return store.updateStatus(id, selectedStatus.value)
        })

        await Promise.all(promises)

        toastStore.setNotification({
            message: `The selected loads have been updated successfully`,
            type: 'success',
        })
    } catch (error) {
        toastStore.setNotification({
            message: error.message(),
            type: 'error',
        })
    } finally {
        isLoading.value = false

        onCancel()
    }
}

/**
 * The on accept handler.
 *
 * @return {Promise}
 */
const onAccept = async () => {
    if (!data.value || !selectedStatus.value) {
        return
    }

    if (Array.isArray(data.value)) {
        return bulkUpdateStatus()
    }

    try {
        isLoading.value = true

        await store.updateStatus(data.value?.id, selectedStatus.value)

        toastStore.setNotification({
            message: `Load status updated successfully`,
            type: 'success',
        })
    } catch (error) {
        toastStore.setNotification({
            message: error.message(),
            type: 'error',
        })
    } finally {
        isLoading.value = false

        onCancel()
    }
}

/**
 * The on cancel handler.
 *
 * @return {void}
 */
const onCancel = () => {
    uiStore.closeAction(UPDATE_STATUS)
}

/**
 * Watch the status changes.
 */
watch(
    () => status,
    (status) => {
        if (!status.value) {
            return
        }

        selectedStatus.value = status.value?.id
    },
    { immediate: true }
)

/**
 * Watch the active status for changes.
 */
watch(
    isActive,
    (value) => {
        show.value = value
    },
    { immediate: true }
)

/**
 * Watch the show value for changes.
 */
watch(show, (value) => {
    if (!value) {
        uiStore.closeAction(UPDATE_STATUS)
    }
})
</script>

<template>
    <v-dialog
        data-test="confirmation-dialog"
        v-model="show"
        persistent
        width="480"
    >
        <v-card class="!rounded-lg">
            <v-card-item
                title="Change load status"
                density="compact"
                class="!py-2 !px-4 bg-gray-100 border-b"
            >
                <template v-slot:append>
                    <v-btn
                        size="x-small"
                        variant="text"
                        icon="fal fa-close"
                        color="primary"
                        class="rounded-md"
                        @click="onCancel"
                    >
                    </v-btn>
                </template>
            </v-card-item>
            <v-card-text class="!px-4" data-test="confirmation-dialog-body">
                <v-form>
                    <div class="text-base mb-5">
                        Change load status for PRO #(s) {{ pros }} to:
                    </div>
                    <v-radio-group
                        v-model="selectedStatus"
                        class="mt-2"
                        variant="outlined"
                        density="comfortable"
                    >
                        <v-radio
                            v-for="status in statuses"
                            :key="status.id"
                            :value="status.id"
                        >
                            <template v-slot:label>
                                <div class="flex items-center">
                                    <span class="w-24">
                                        {{ status?.name }}
                                    </span>
                                </div>
                            </template>
                        </v-radio>
                    </v-radio-group>
                </v-form>
            </v-card-text>
            <div class="bg-gray-100 border-t py-2 px-4">
                <div class="text-xs">
                    Changing from <b>{{ status?.name }}</b> to another status
                    could cause issues for the currently assigned driver and
                    truck.
                </div>
            </div>
            <v-spacer class="border-b"></v-spacer>
            <v-card-actions class="px-4 justify-end">
                <v-btn
                    data-test="confirmation-dialog-btn-close"
                    variant="outlined"
                    @click="onCancel"
                >
                    Cancel
                </v-btn>
                <v-btn
                    color="primary"
                    variant="flat"
                    :loading="isLoading"
                    :disabled="!selectedStatus"
                    data-test="confirmation-dialog-btn-accept"
                    @click="onAccept"
                >
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
